import { useRef, useState } from 'react';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  FlagOutlined,
  ReadOutlined,
  StrikethroughOutlined,
  UpOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Divider,
  Flex,
  Radio,
  Space,
  Tabs,
  Tag,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import { nomeDaQuestao } from '@titularis/ui';
import {
  AVISO_DO_TETO_DIARIO,
  ehTetoDiario,
  motivoDoBloqueio,
  type MotivoDeBloqueio,
  type StudySummary,
} from '@titularis/api-client';
import { escolher, riscar, SEM_DECISAO } from '../alternativas';
import { api } from '../api';
import { registrarEvento } from '../analytics';
import { useTemHover } from '../hover';
import { ConteudoRico } from './ConteudoRico';
import './questao-card.css';
import { ReportarErro } from './ReportarErro';

/**
 * Uma questão na lista de estudo.
 *
 * O fluxo é o do escopo: escolher a alternativa habilita "Responder"; responder
 * mostra acerto ou erro; a resolução é um passo à parte, com cota própria.
 *
 * Nada aqui decide acesso. Quando o backend nega, o motivo que vem no erro
 * sobe para a tela, que abre o paywall (G-POLICY).
 */
const CAMPOS_DO_COMENTARIO = [
  { chave: 'legislation', titulo: 'Legislação' },
  { chave: 'jurisprudence', titulo: 'Jurisprudência' },
  { chave: 'doctrine', titulo: 'Doutrina' },
] as const;

/**
 * Nas objetivas, o comentário só abre depois de responder. O botão fica
 * bloqueado e a mensagem aparece no hover, sem esperar o backend - que continua
 * sendo quem realmente barra a abertura (G-POLICY).
 */
const RESPONDA_PRIMEIRO =
  'Queremos ajudar no seu aprendizado: responda a questão antes de ver o comentário. Assim você fixa o raciocínio e o comentário rende mais.';

/**
 * O lápis da prova impressa.
 *
 * Eliminar o que não serve antes de escolher é como se resolve questão de
 * múltipla escolha no papel, e até agora o aluno fazia isso de cabeça: quatro
 * alternativas longas de cartório, e ele relia a mesma que já tinha descartado
 * duas vezes. O risco é rascunho - não vai para o servidor e morre com a tela -,
 * então ele pode errar à vontade, que nada fica registrado.
 */
const RISCAR = 'Riscar esta alternativa';
const DESFAZER_O_RISCO = 'Trazer esta alternativa de volta';

/**
 * A letra da alternativa.
 *
 * Largura fixa e sem quebra: com o texto longo das questões de cartório, o
 * "A)" chegava a quebrar entre a letra e o parêntese, e as alternativas
 * ficavam desalinhadas entre si.
 */
function Letra({ letra, riscada = false }: { letra: string; riscada?: boolean }) {
  return (
    /*
     * Riscada, a letra recua com o texto. O `type` existe porque o
     * `Typography.Text` fixa a própria cor, e sem ele a letra ficaria em preto
     * cheio ao lado de um texto cinza - a alternativa que o aluno descartou
     * chamaria mais atenção que as que ele ainda considera.
     */
    <Typography.Text
      strong
      type={riscada ? 'secondary' : undefined}
      style={{ minWidth: 26, whiteSpace: 'nowrap' }}
    >
      {letra.toUpperCase()})
    </Typography.Text>
  );
}

export function QuestaoCard({
  questao,
  onBloqueio,
}: {
  questao: StudySummary;
  onBloqueio: (motivo: MotivoDeBloqueio, origem: 'answer' | 'commentary') => void;
}) {
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  /*
   * Marcar e riscar são gestos sobre a mesma lista, e por isso um estado só.
   * A regra que os liga - a escolhida nunca está riscada - vive em
   * `alternativas.ts`, com teste, porque é regra e não desenho de tela.
   */
  const [decisao, setDecisao] = useState(SEM_DECISAO);
  const [reportando, setReportando] = useState(false);

  /*
   * Só quem paira recebe dica.
   *
   * No toque não existe pairar: o navegador emula o `hover` no próprio toque, e
   * a dica do risco abria **junto com o risco** - uma tarja preta sobre a
   * alternativa vizinha, dizendo o que o aluno acabou de fazer e ficando na
   * tela até ele tocar em outro lugar.
   *
   * Vale para esta dica, e não para todas: a do "Ver comentários" desabilitado
   * é a única forma de ler por que ele está desabilitado, e no celular o toque
   * é o único jeito de chamá-la.
   */
  const temHover = useTemHover();

  const discursiva = Boolean(questao.essayKind);

  /*
   * **Anulada pela banca** (D-61): está no ar para ser entendida, e não para ser
   * respondida. Não tem gabarito oficial nem veredito, então as alternativas
   * ficam só para leitura e o comentário abre direto - é ele que explica por
   * que a banca a anulou. A API recusa a resposta de qualquer jeito (G-POLICY);
   * aqui só não se oferece o que não vai dar certo.
   */
  const anulada = questao.status === 'annulled';

  // O nome vem do domínio compartilhado: o backoffice usa o mesmo, e a mesma
  // questão não pode ter dois nomes conforme a tela. Ver `ui/questoes.ts`.
  const tituloDaQuestao = nomeDaQuestao(questao);

  // Cada nível reúne os itens da questão; níveis vazios somem da trilha.
  const nomes = (itens: { name: string }[]) => itens.map((item) => item.name).join(', ');
  const trilhaDeClassificacao = [
    nomes(questao.disciplines),
    nomes(questao.subjects),
    nomes(questao.subjectDetails),
  ]
    .filter(Boolean)
    .join(' › ');

  const responder = useMutation({
    mutationFn: () => api.answerStudyQuestion(questao.id, decisao.escolhida!),
    onSuccess: (resultado) => {
      registrarEvento({
        nome: 'question_answered',
        questionId: questao.id,
        module: questao.module,
        isCorrect: resultado.isCorrect,
      });
      void queryClient.invalidateQueries({ queryKey: ['study-status'] });
    },
    onError: (erro: Error) => {
      /*
       * O teto diário não é paywall nem erro: quem o encontra assinou e leu
       * muito hoje (D-50). Aviso, e não a oferta - oferecer a quem já comprou
       * é o defeito de 03/09/2026 pelo avesso.
       */
      if (ehTetoDiario(erro)) {
        message.info(AVISO_DO_TETO_DIARIO);
        return;
      }

      const motivo = motivoDoBloqueio(erro);
      if (motivo) onBloqueio(motivo, 'answer');
      else message.error(erro.message);
    },
  });

  const desfazer = useMutation({
    mutationFn: () => api.undoStudyAnswer(questao.id),
    onSuccess: async () => {
      responder.reset();
      // Refazer é recomeçar: o rascunho do aluno sai junto com a resposta.
      setDecisao(SEM_DECISAO);
      await queryClient.invalidateQueries({ queryKey: ['study-questions'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const resolucao = useMutation({
    mutationFn: () => api.revealCommentary(questao.id),
    onSuccess: () => {
      registrarEvento({
        nome: 'commentary_opened',
        questionId: questao.id,
        module: questao.module,
      });
      void queryClient.invalidateQueries({ queryKey: ['study-status'] });
    },
    onError: (erro: Error) => {
      /*
       * O teto diário não é paywall nem erro: quem o encontra assinou e leu
       * muito hoje (D-50). Aviso, e não a oferta - oferecer a quem já comprou
       * é o defeito de 03/09/2026 pelo avesso.
       */
      if (ehTetoDiario(erro)) {
        message.info(AVISO_DO_TETO_DIARIO);
        return;
      }

      const motivo = motivoDoBloqueio(erro);
      if (motivo) onBloqueio(motivo, 'commentary');
      else message.error(erro.message);
    },
  });

  /*
   * A resposta é do servidor, não da tela. Recarregar a página, voltar depois
   * ou abrir no celular mostra o que o aluno já respondeu - o que ele fez não
   * pode depender de a aba continuar aberta.
   */
  const respondida = responder.data ?? questao.minhaResposta ?? undefined;
  const conteudoDaResolucao = resolucao.data;

  // Objetiva ainda não respondida: a resolução fica bloqueada, com aviso no hover.
  // A anulada não se responde, então não bloqueia.
  const precisaResponder = !discursiva && !anulada && !respondida;

  // Comentário de cada alternativa, indexado por letra. Só existe após abrir a resolução.
  const comentarioDoItem = new Map(
    (conteudoDaResolucao?.optionCommentaries ?? []).map((item) => [item.letter, item.html]),
  );

  /*
   * As abas do material de estudo. Espelho da banca e Modelo de Resposta são
   * coisas distintas - o padrão oficial da prova e a resposta ideal do
   * especialista - e aparecem separados para o aluno não confundir os dois.
   * Campo vazio não vira aba: o escopo é explícito que o aluno não deve ver
   * caixa em branco.
   */
  /*
   * A aba passa a ser controlada por causa da citação interna: sem `activeKey`,
   * não há como o link do comentário levar o aluno até a aba certa.
   */
  const [abaAtiva, setAbaAtiva] = useState<string>();
  /*
   * Depois de revelado, o comentário pode ser recolhido e reaberto **sem
   * chamar a API de novo**: revelar consome cota semanal do aluno, e cobrar de
   * novo por fechar e reabrir seria punir quem organizou a própria tela.
   */
  const [comentariosVisiveis, setComentariosVisiveis] = useState(true);
  const refDasAbas = useRef<HTMLDivElement>(null);

  /**
   * Leva o aluno até a aba citada.
   *
   * Confere direto na fonte, e não na lista de abas montada abaixo: além de
   * quebrar a dependência circular entre as duas, é mais preciso - citação só
   * aponta para aba de comentário, nunca para espelho ou modelo de resposta.
   *
   * Aba sem conteúdo não troca nada: o comentário pode citar uma aba que ficou
   * vazia, e nesse caso o clique não faz nada - melhor que abrir aba em branco
   * ou rolar para lugar nenhum, que pareceriam defeito.
   */
  const irParaCitacao = (aba: string) => {
    const temConteudo = CAMPOS_DO_COMENTARIO.some(
      (campo) => campo.chave === aba && conteudoDaResolucao?.commentary?.[campo.chave]?.html,
    );
    if (!temConteudo) return;

    setAbaAtiva(aba);
    refDasAbas.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const abas = [
    ...(conteudoDaResolucao?.answerKeyHtml
      ? [
          {
            key: 'answerKey',
            label: 'Espelho da banca',
            children: (
              <ConteudoRico
                html={conteudoDaResolucao.answerKeyHtml}
                onCitacao={irParaCitacao}
                protegido
              />
            ),
          },
        ]
      : []),
    ...(conteudoDaResolucao?.answerModelHtml
      ? [
          {
            key: 'answerModel',
            label: 'Modelo de Resposta',
            children: (
              <ConteudoRico
                html={conteudoDaResolucao.answerModelHtml}
                onCitacao={irParaCitacao}
                protegido
              />
            ),
          },
        ]
      : []),
    ...CAMPOS_DO_COMENTARIO.filter((campo) => conteudoDaResolucao?.commentary?.[campo.chave]?.html)
      .map((campo) => ({
        key: campo.chave,
        label: campo.titulo,
        children: (
          <ConteudoRico
            html={conteudoDaResolucao!.commentary[campo.chave]!.html}
            onCitacao={irParaCitacao}
            protegido
          />
        ),
      })),
  ];


  return (
    <Card
      size="small"
      styles={{
        /*
         * O contorno tracejado é o que distingue a anulada rolando a lista,
         * antes de se ler a etiqueta: o tracejado lê como "fora da conta", e
         * no tom de alerta do tema ele acompanha a etiqueta e o aviso.
         */
        root: anulada ? { borderStyle: 'dashed', borderColor: token.colorWarningBorderHover } : undefined,
        body: { padding: 16 },
      }}
      title={
        /*
         * O cabeçalho em três degraus: título, prova, classificação.
         *
         * O título é a identidade da questão, e não a posição dela na busca.
         * Havia os dois - etiqueta com o índice da lista e título com o número
         * da prova -, e lado a lado eram dois números sem rótulo que ninguém
         * distinguia. Ficou o estável: o número da prova não muda quando o
         * aluno troca o filtro, é o que está impresso na prova e é o que duas
         * pessoas conseguem usar para falar da mesma questão. O índice da lista
         * muda a cada busca, e quem diz onde o aluno está é a paginação.
         *
         * Os três já estiveram no mesmo peso - o número da questão era uma
         * etiqueta solta e o resto, cinza secundário -, e um cartão sem nada em
         * destaque não tem por onde o olho entrar. Rolando uma lista, o aluno
         * perdia a conta de onde estava.
         *
         * Agora o número é título de verdade: 18px sobre corpo de 15, com peso.
         * Prova e classificação seguem secundárias, em 15 e 12 - a diferença
         * entre elas passou a ser tamanho, e não só ordem na linha.
         */
        <Space orientation="vertical" size={2} style={{ paddingBlock: 8, whiteSpace: 'normal' }}>
          <Flex align="center" justify="space-between" gap={8} wrap>
            <Typography.Title level={5} style={{ margin: 0 }}>
              {tituloDaQuestao}
            </Typography.Title>
            {/* As etiquetas vão para a direita do título: no meio da linha elas
                empurravam o número e disputavam o destaque com ele. */}
            <Flex gap={4} wrap>
              {anulada ? (
                <Tag color="warning" style={{ marginInlineEnd: 0 }}>
                  Anulada
                </Tag>
              ) : null}
              {questao.isFree ? (
                <Tag color="green" style={{ marginInlineEnd: 0 }}>
                  Grátis
                </Tag>
              ) : null}
            </Flex>
          </Flex>
          {/*
            Banca e prova identificam a origem da questão.

            O ano saiu porque o nome da prova já o traz - "ENAC, 2025.1" - e
            repeti-lo ao lado dava "ENAC, 2025.1 · 2025", que faz o leitor parar
            para conferir se são a mesma coisa. Vale enquanto a convenção de
            nomear a prova com o ano se mantiver; uma prova cadastrada sem ano
            no nome perde a informação aqui.
          */}
          <Typography.Text type="secondary">
            {questao.boardName} · {questao.examName}
          </Typography.Text>
          {/* A trilha de classificação: disciplinas › assuntos › delimitações,
              cada nível com os vários itens da questão separados por vírgula. */}
          {trilhaDeClassificacao ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {trilhaDeClassificacao}
            </Typography.Text>
          ) : null}
        </Space>
      }
    >
      <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
        {/*
          O aviso vem antes do enunciado: quem lê a questão inteira achando que
          ela vale e só no fim descobre que não vale perdeu a leitura.
        */}
        {anulada ? (
          <Alert
            type="warning"
            showIcon
            title="Questão anulada pela banca"
            description="Ela não vale ponto e não tem gabarito oficial. As alternativas ficam para leitura, e o comentário explica o motivo da anulação."
          />
        ) : null}

        <ConteudoRico html={questao.statementHtml} />

        {discursiva ? null : respondida || anulada ? (
          /*
           * Depois de responder, as alternativas viram leitura - e a anulada
           * nasce assim, porque não se responde.
           *
           * O caminho óbvio seria desabilitar o grupo de rádios, mas desabilitado
           * no Antd apaga o texto - e é justamente agora que o aluno precisa
           * reler cada alternativa para entender por que errou.
           */
          <Space orientation="vertical" size={10} style={{ width: '100%' }}>
            {questao.options.map((alternativa) => {
              const certa = respondida?.correctOption === alternativa.letter;
              const marcada = respondida?.selectedOption === alternativa.letter;
              /*
               * Só depois de abrir a resolução, só nos itens que têm comentário
               * e só enquanto os comentários estiverem abertos.
               *
               * O `comentariosVisiveis` faltava aqui: "Fechar comentários"
               * recolhia as abas e deixava os comentários dos itens na tela.
               * O botão está no plural e não distingue um tipo do outro - quem
               * fecha quer a questão limpa para reler as alternativas.
               */
              const comentario =
                conteudoDaResolucao && comentariosVisiveis
                  ? comentarioDoItem.get(alternativa.letter)
                  : undefined;

              /*
               * Sem gabarito - questão anulada - não há veredito a afirmar.
               *
               * A alternativa que o aluno marcou continua marcada, mas em tom
               * neutro: pintá-la de vermelho diria que ele errou, e numa questão
               * anulada ninguém errou. É o desenho afirmando o que o dado não
               * sustenta.
               */
              const temGabarito = Boolean(respondida?.correctOption);
              const erradaEscolhida = marcada && temGabarito && !certa;

              /*
               * O contorno inteiro leva o tom do veredito, em vez de uma barra
               * lateral grossa. Cerca o par sem criar um lado pesado, e a cor
               * forte segue onde já estava: no fundo da alternativa e no ícone.
               *
               * Nos itens sem veredito ele é discreto - não há o que destacar -,
               * e é por isso que a divisória não acompanha: ela é o que de fato
               * separa a alternativa do comentário, e precisa ser linha visível
               * em todos os itens. Deixá-la fraca justamente nos neutros seria
               * recriar o defeito que começou tudo isto.
               */
              const contorno = certa
                ? token.colorSuccessBorder
                : erradaEscolhida
                  ? token.colorErrorBorder
                  : token.colorBorderSecondary;

              return (
                /*
                 * A alternativa e o comentário dela são um bloco só.
                 *
                 * Eram dois retângulos soltos, e o de baixo se distinguia por um
                 * fundo de 2% de preto (`colorFillQuaternary`). Dois por cento
                 * some: em tela de celular com brilho automático, sob luz do dia,
                 * aquele fundo simplesmente não existe - e o comentário virava
                 * continuação do texto da alternativa.
                 *
                 * Agora o que separa é estrutura, não tom: o contorno que cerca
                 * o par, a divisória entre os dois e o fundo do comentário.
                 * Linha sobrevive ao sol; véu de 2% não.
                 */
                <div
                  key={alternativa.letter}
                  style={{
                    border: `1px solid ${contorno}`,
                    borderRadius: token.borderRadius,
                    // Sem isto o fundo do comentário escapa pelos cantos
                    // arredondados do bloco.
                    overflow: 'hidden',
                  }}
                >
                  <Flex
                    align="flex-start"
                    gap={10}
                    style={{
                      padding: 8,
                      background: certa
                        ? token.colorSuccessBg
                        : erradaEscolhida
                          ? token.colorErrorBg
                          : undefined,
                    }}
                  >
                    <Letra letra={alternativa.letter} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <ConteudoRico html={alternativa.html} />
                    </div>
                    {certa ? (
                      <CheckCircleFilled style={{ color: token.colorSuccess, marginTop: 4 }} />
                    ) : erradaEscolhida ? (
                      <CloseCircleFilled style={{ color: token.colorError, marginTop: 4 }} />
                    ) : null}
                  </Flex>
                  {comentario ? (
                    <div
                      style={{
                        borderTop: `1px solid ${token.colorBorder}`,
                        background: token.colorFillTertiary,
                        padding: '8px 10px',
                      }}
                    >
                      {/*
                        O rótulo diz o veredito em palavras, e não em cor: verde
                        e vermelho do tema dão 2.27 e 3.27 de contraste sobre
                        branco, e texto precisa de 4.5. A cor fica na trilha, que
                        é elemento gráfico - quem lê no sol continua sabendo se
                        o item estava certo, mesmo sem enxergar o verde.

                        Em caixa alta e espaçado, o rótulo virava etiqueta de
                        formulário: chamava mais atenção que o comentário que
                        anuncia, e "ERRADA" gritado antes de cada explicação é
                        rigor a mais para quem está estudando. Em caixa normal
                        ele segue sendo rótulo - o negrito e o cinza bastam -,
                        e a frase se lê na velocidade em que se lê texto.
                      */}
                      <Typography.Text
                        type="secondary"
                        style={{
                          display: 'block',
                          fontSize: 12,
                          fontWeight: 600,
                          marginBottom: 4,
                        }}
                      >
                        {temGabarito
                          ? `Por que a alternativa está ${certa ? 'certa' : 'errada'}:`
                          : `Comentário do item ${alternativa.letter.toUpperCase()}`}
                      </Typography.Text>
                      {/* É daqui que a citação interna costuma partir: "conforme
                          a legislação" leva o aluno à aba, sem tirá-lo da questão. */}
                      <ConteudoRico html={comentario} onCitacao={irParaCitacao} protegido />
                    </div>
                  ) : null}
                </div>
              );
            })}
          </Space>
        ) : (
          <Radio.Group
            value={decisao.escolhida}
            /*
             * Atualização por função, e não pelo `decisao` do render: dois
             * gestos dentro do mesmo lote de renderização leriam o mesmo estado
             * antigo, e o segundo apagaria o primeiro. Medido riscando duas
             * alternativas de uma vez - só a última ficou riscada.
             */
            onChange={(evento) =>
              setDecisao((atual) => escolher(atual, evento.target.value as string))
            }
            style={{ width: '100%' }}
          >
            <Space orientation="vertical" size={10} style={{ width: '100%' }}>
              {questao.options.map((alternativa) => {
                const riscada = decisao.riscadas.includes(alternativa.letter);

                const botaoDeRiscar = (
                  <Button
                    /*
                     * Quem mostra o estado é a alternativa riscada, não o
                     * botão: o `variant="filled"` do Antd entrega 4% de preto,
                     * que é o mesmo véu que já tinha sumido no sol aqui
                     * embaixo, no comentário do item. O botão só fica um tom
                     * mais presente quando está ligado.
                     */
                    type="text"
                    size="small"
                    icon={<StrikethroughOutlined />}
                    styles={{
                      icon: { color: riscada ? token.colorText : token.colorTextSecondary },
                    }}
                    /*
                     * O nome acessível diz o que o clique faz, porque o ícone
                     * sozinho não diz - e `aria-pressed` é o que anuncia que
                     * este botão tem dois estados.
                     *
                     * É também o que sobra quando a dica sai, no celular: o
                     * leitor de tela continua lendo a mesma frase, porque ela
                     * nunca dependeu da dica.
                     */
                    aria-label={riscada ? DESFAZER_O_RISCO : RISCAR}
                    aria-pressed={riscada}
                    // Alinha com a primeira linha do texto, e não com o meio da
                    // alternativa: é o mesmo respiro de 8 que o rádio ao lado usa.
                    style={{ marginTop: 8 }}
                    onClick={() => setDecisao((atual) => riscar(atual, alternativa.letter))}
                  />
                );

                return (
                  /*
                   * O botão de riscar é irmão do rádio, e não filho dele: dentro
                   * do rótulo, todo clique no botão marcaria a alternativa antes
                   * de riscá-la, e riscar seria o gesto que escolhe.
                   */
                  <Flex key={alternativa.letter} align="flex-start" gap={4} style={{ width: '100%' }}>
                    <Radio
                      className="titularis-alternativa"
                      value={alternativa.letter}
                      style={{ flex: 1, minWidth: 0, alignItems: 'flex-start', padding: 8 }}
                    >
                      {/*
                        Flex, e não Space: o Space embrulha cada filho num item que
                        encolhe, e a letra quebrava no meio - "A" numa linha e ")"
                        na seguinte.
                      */}
                      <Flex
                        align="flex-start"
                        gap={10}
                        /*
                         * O traço atravessa a alternativa inteira, letra
                         * inclusive: é o que a caneta faz no papel, e é por
                         * isso que ele mora aqui, no que embrulha os dois, e
                         * não no texto.
                         *
                         * O cinza é `colorTextSecondary`, e não o terciário: a
                         * alternativa riscada continua sendo lida - é
                         * justamente relendo que o aluno muda de ideia -, e o
                         * terciário fica em 3:1, abaixo dos 4.5 que texto
                         * corrido pede.
                         */
                        style={
                          riscada
                            ? { textDecoration: 'line-through', color: token.colorTextSecondary }
                            : undefined
                        }
                      >
                        <Letra letra={alternativa.letter} riscada={riscada} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <ConteudoRico html={alternativa.html} />
                        </div>
                      </Flex>
                    </Radio>
                    {/*
                      No toque a dica não entra na árvore: `trigger` vazio ainda
                      montaria o portal e a lógica de posição para nada.
                    */}
                    {temHover ? (
                      <Tooltip title={riscada ? DESFAZER_O_RISCO : RISCAR}>
                        {botaoDeRiscar}
                      </Tooltip>
                    ) : (
                      botaoDeRiscar
                    )}
                  </Flex>
                );
              })}
            </Space>
          </Radio.Group>
        )}

        {respondida ? (
          <Alert
            type={respondida.isCorrect ? 'success' : 'error'}
            showIcon
            icon={respondida.isCorrect ? <CheckCircleFilled /> : <CloseCircleFilled />}
            title={
              respondida.isCorrect
                ? 'Você acertou.'
                : `Você marcou ${respondida.selectedOption.toUpperCase()}. O gabarito é ${(respondida.correctOption ?? '').toUpperCase()}.`
            }
          />
        ) : null}

        <Space wrap>
          {/* Respondida ou anulada, o botão sai de cena: botão morto na tela é ruído. */}
          {discursiva || anulada || respondida ? null : (
            <Button
              type="primary"
              disabled={!decisao.escolhida}
              loading={responder.isPending}
              onClick={() => responder.mutate()}
            >
              Responder
            </Button>
          )}

          {respondida ? (
            <Button
              icon={<UndoOutlined />}
              // Verde no acerto, vermelho no erro: o botão reforça o resultado.
              color={respondida.isCorrect ? 'green' : 'red'}
              variant="solid"
              loading={desfazer.isPending}
              // O ícone do Antd entra no nome acessível ("undo Refazer
              // questão"); o rótulo explícito é o que o leitor de tela anuncia.
              aria-label="Refazer questão"
              onClick={() => desfazer.mutate()}
            >
              Refazer questão
            </Button>
          ) : null}

          <Tooltip title={precisaResponder ? RESPONDA_PRIMEIRO : ''}>
            <Button
              icon={
                conteudoDaResolucao && comentariosVisiveis ? <UpOutlined /> : <ReadOutlined />
              }
              loading={resolucao.isPending}
              disabled={precisaResponder}
              onClick={() =>
                conteudoDaResolucao
                  ? setComentariosVisiveis((visivel) => !visivel)
                  : resolucao.mutate()
              }
            >
              {conteudoDaResolucao
                ? comentariosVisiveis
                  ? 'Fechar comentários'
                  : 'Ver comentários'
                : discursiva
                  ? 'Ver espelho e comentários'
                  : 'Ver comentários'}
            </Button>
          </Tooltip>

          <Button type="text" icon={<FlagOutlined />} onClick={() => setReportando(true)}>
            Reportar erro
          </Button>
        </Space>

        {conteudoDaResolucao && comentariosVisiveis ? (
          abas.length > 0 ? (
            <>
              <Divider style={{ margin: 0 }} />
              <div ref={refDasAbas}>
                <Tabs
                  items={abas}
                  activeKey={abaAtiva ?? abas[0]?.key}
                  onChange={setAbaAtiva}
                />
              </div>
            </>
          ) : (
            <Alert
              type="info"
              showIcon
              title="Esta questão ainda não tem comentário publicado."
            />
          )
        ) : null}
      </Space>

      <ReportarErro
        questionId={questao.id}
        discursiva={discursiva}
        anulada={anulada}
        aberto={reportando}
        onClose={() => setReportando(false)}
      />
    </Card>
  );
}
